import React, { useState, useMemo, useEffect } from 'react';
import { Employee, ShiftAssignment, EmployeeStatus } from '../types';
import Pagination from './Pagination';
import { ITEMS_PER_PAGE, PencilIcon, TrashIcon } from '../constants';
import { loadFromLocalStorage, saveToLocalStorage } from '../utils/localStorageUtils';

type SortField = 'employeeId' | 'firstName' | 'lastName' | 'status';
type SortDirection = 'asc' | 'desc';

interface SortState {
  field: SortField;
  direction: SortDirection;
}

interface EmployeeTableProps {
  employees: Employee[];
  onModify: (employee: Employee) => void;
  onDelete: (employee: Employee) => void;
}

const SORT_STORAGE_KEY = 'yieldTrackApp_employeeTableSort';

const EmployeeTable: React.FC<EmployeeTableProps> = ({ employees, onModify, onDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>(() => 
    loadFromLocalStorage(SORT_STORAGE_KEY, { field: 'employeeId', direction: 'asc' })
  );

  // Save sort state to localStorage whenever it changes
  useEffect(() => {
    saveToLocalStorage(SORT_STORAGE_KEY, sortState);
  }, [sortState]);

  // Handle column header clicks for sorting
  const handleSort = (field: SortField) => {
    setSortState(prevState => ({
      field,
      direction: prevState.field === field && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const formatShiftAssignment = (shiftAssignment: ShiftAssignment): string => {
    switch (shiftAssignment) {
      case ShiftAssignment.SHIFT_1:
        return 'Shift 1';
      case ShiftAssignment.SHIFT_2:
        return 'Shift 2';
      case ShiftAssignment.BOTH_SHIFTS:
        return 'Both Shifts';
      default:
        return 'Unknown';
    }
  };

  const getShiftBadgeColor = (shiftAssignment: ShiftAssignment): string => {
    switch (shiftAssignment) {
      case ShiftAssignment.SHIFT_1:
        return 'bg-blue-100 text-blue-800';
      case ShiftAssignment.SHIFT_2:
        return 'bg-green-100 text-green-800';
      case ShiftAssignment.BOTH_SHIFTS:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: EmployeeStatus): string => {
    switch (status) {
      case EmployeeStatus.ACTIVE:
        return 'bg-emerald-100 text-emerald-800';
      case EmployeeStatus.SUSPENDED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: EmployeeStatus): string => {
    switch (status) {
      case EmployeeStatus.ACTIVE:
        return 'Active';
      case EmployeeStatus.SUSPENDED:
        return 'Suspended';
      default:
        return 'Unknown';
    }
  };

  // Sort employees based on current sort state
  const sortedEmployees = useMemo(() => {
    const sorted = [...employees].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortState.field) {
        case 'employeeId':
          aValue = a.employeeId.toLowerCase();
          bValue = b.employeeId.toLowerCase();
          break;
        case 'firstName':
          aValue = a.firstName.toLowerCase();
          bValue = b.firstName.toLowerCase();
          break;
        case 'lastName':
          aValue = a.lastName.toLowerCase();
          bValue = b.lastName.toLowerCase();
          break;
        case 'status':
          aValue = formatStatus(a.status);
          bValue = formatStatus(b.status);
          break;
        default:
          aValue = a.employeeId.toLowerCase();
          bValue = b.employeeId.toLowerCase();
      }

      if (aValue < bValue) return sortState.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [employees, sortState]);

  const totalPages = Math.ceil(sortedEmployees.length / ITEMS_PER_PAGE);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const lastPageIndex = firstPageIndex + ITEMS_PER_PAGE;
    return sortedEmployees.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, sortedEmployees]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Sortable column header component
  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className = "" }) => {
    const isActive = sortState.field === field;
    const direction = isActive ? sortState.direction : 'asc';
    
    return (
      <th scope="col" className={`px-4 py-3 ${className}`}>
        <button
          onClick={() => handleSort(field)}
          className="flex items-center space-x-1 text-xs text-slate-500 uppercase font-medium hover:text-slate-700 focus:outline-none focus:text-slate-700 transition-colors"
        >
          <span>{children}</span>
          <div className="flex flex-col">
            <svg
              className={`w-3 h-3 ${isActive && direction === 'asc' ? 'text-indigo-600' : 'text-slate-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <svg
              className={`w-3 h-3 -mt-1 ${isActive && direction === 'desc' ? 'text-indigo-600' : 'text-slate-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </button>
      </th>
    );
  };

  if (employees.length === 0) {
    return <p className="text-slate-600 italic text-center py-8">No employees found.</p>;
  }

  return (
    <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm text-left text-slate-600">
          <thead className="bg-slate-50">
            <tr>
              <SortableHeader field="employeeId">Employee ID</SortableHeader>
              <SortableHeader field="firstName">First Name</SortableHeader>
              <SortableHeader field="lastName">Last Name</SortableHeader>
              <th scope="col" className="px-4 py-3 text-xs text-slate-500 uppercase font-medium">Shift Assignment</th>
              <SortableHeader field="status">Status</SortableHeader>
              <th scope="col" className="px-4 py-3 text-center text-xs text-slate-500 uppercase font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTableData.map((employee) => (
              <tr key={employee.id} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{employee.employeeId}</td>
                <td className="px-4 py-3">{employee.firstName}</td>
                <td className="px-4 py-3">{employee.lastName}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getShiftBadgeColor(employee.shiftAssignment)}`}>
                    {formatShiftAssignment(employee.shiftAssignment)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(employee.status)}`}>
                    {formatStatus(employee.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => onModify(employee)}
                      className="p-3 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Modify Employee"
                      aria-label={`Modify employee ${employee.firstName} ${employee.lastName}`}
                    >
                      <PencilIcon className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => onDelete(employee)}
                      className="p-3 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Delete Employee"
                      aria-label={`Delete employee ${employee.firstName} ${employee.lastName}`}
                    >
                      <TrashIcon className="w-6 h-6" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default EmployeeTable;
